import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  deleteFastTranslateSeriesItemApi,
  getFastTranslateSeriesItemListApi,
} from "../../service/fastTranslateSeries";
import { getTaskListApi } from "../../service/folders";
import { autoSubtitleApi, getPureVideoListApi, postTaskPointPreviewApi } from "../../service/task";
import { delTaskApi, submitResourceApi, uploadSingleFileToTos } from "../../service/myWork";
import { getSeriesListApi } from "../../service/series";
import { Button, Modal } from "antd";
import { MAX_UPLOAD_VIDEO_EPISODES } from "../../config/constants";
import { AlertMessage, AutoModel, UploadProgressOverlay } from "../../components/common";
import { useAlert } from "../../hooks/useAlert";
import { useSeriesButtonPermissions } from "../../hooks/useSeriesButtonPermissions";
import { ALERT_Z_INDEX } from "../../utils/alertManager";
import { mapFastTranslateSeriesItemListToTaskItems } from "../../utils/fastTranslateVideoListAdapter";
import { getVideoPlayUrl, processVideoUrl } from "../../utils/videoUrl";
import { PageBackLink } from "../home/PageBack";
import { CoinsEstimateIcon } from "../../components/common/CoinsEstimateIcon";
import { useHasPermission } from "../../hooks/useHasPermission";
import { PERMISSIONS } from "../../constants/permissionKeys";
import { PREMIUM_VIDEO_LIST_REFACTOR_BASE } from "../../utils/menuPathNormalize";
import { VideoListRefactorLucideIcon } from "../videoListRefactor/videoListRefactorLucideIcons";
import { getVideoMetadata } from "../../utils/videoMetadata";
import { validateVideoUploadFileName, getVideoUploadFileNameInvalidMessage } from "../../utils/videoFileName";
import { uploadFastTranslateWorkspaceVideo } from "./uploadFastTranslateVideoFile";
import { triggerMembershipRefresh } from "../../service/membership";
import {
  PremiumAutoSubtitleModal,
  type PremiumAutoSubtitleModalHandle,
  type PremiumAutoSubtitleParamsPreview,
  type PremiumAutoSubtitleVideoRow,
} from "./PremiumAutoSubtitleModal";
import "./PremiumUploadVideoWorkspacePage.scss";

type SubtitleMode = "manual" | "auto";

/** 与旧版 Index.tsx `ScriptFile` 一致：同名检测、上传态、列表回显 */
type ScriptPanelFile = {
  uid: string;
  name: string;
  size: number;
  type: string;
  status: "done" | "uploading" | "error";
  file?: File;
  resourceId?: number;
  abortController?: AbortController;
};

/** 与旧版 Index.tsx `checkScriptFileName` 一致：剧本主名须与剧集名称一致 */
const checkScriptFileNameAgainstSeries = (
  scriptFileName: string,
  seriesName: string
): { isValid: boolean; message: string } => {
  const scriptNameWithoutExt = scriptFileName.replace(/\.[^/.]+$/, "");
  if (scriptNameWithoutExt !== seriesName) {
    return {
      isValid: false,
      message: `您的剧本文件名称"${scriptNameWithoutExt}"与剧集名称"${seriesName}"不一致\n注:剧本文件名称与剧集名称不一致可能会导致翻译配音效果不佳`,
    };
  }
  return { isValid: true, message: "" };
};

/** 与旧版 Index SubtitleFile：手动字幕列表 */
type SubtitlePanelFile = {
  uid: string;
  name: string;
  size: number;
  type: string;
  status: "done" | "uploading" | "error";
  file?: File;
  resourceId?: number;
  abortController?: AbortController;
};

type SubtitleMatchInfo = {
  invalidFiles: { file: File; name: string }[];
  allFiles: File[];
};

/** 与旧版 Index.tsx `videoFiles` 一致：单数组、uid 稳定、上传态在同一行就地变更，不拆 episodes/pending 两套 */
type PremiumVideoStripStatus = "queued" | "uploading" | "uploaded" | "error" | "done";

type PremiumVideoFile = {
  uid: string;
  name: string;
  size: number;
  type: string;
  status: PremiumVideoStripStatus;
  previewUrl?: string;
  thumbnailUrl?: string;
  file?: File;
  durationSeconds?: number;
  progress: number;
  errorMessage?: string;
  decodeFailed?: boolean;
  abortController?: AbortController;
  /** 对应旧版 resourceId / 精品任务 id */
  resourceId?: number;
  remoteCoverUrl?: string;
  uploadedMatchKey?: string;
  uploadedSourceFileKey?: string;
  /** 接口拉取后的整集数据（与旧版「已上传 done + 列表项」一致） */
  episode?: WorkspaceEpisode;
  /** 与旧版 Index VideoFile：自动识别字幕配置写回条带项 */
  subtitleExtractType?: string | null;
  ocrBbox?: string[] | null;
};

type WorkspaceEpisode = {
  id: number;
  displayName: string;
  durationLabel: string;
  durationSeconds: number;
  coverUrl: string;
  statusCode: number;
  playRow: {
    id: number;
    name: string;
    status: number;
    create_time: string;
    descript?: string | null;
    result_url?: string | null;
    pure_video_url?: string | null;
    file_url?: string | null;
    tag: number;
  };
};

const formatDuration = (seconds?: number | null): string => {
  const safe = Number.isFinite(seconds) ? Number(seconds) : 0;
  const m = Math.floor(safe / 60);
  const s = Math.floor(safe % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const baseName = (fileName: string): string => fileName.replace(/\.[^/.]+$/, "");

/** 与旧版 Index `video-preview-grid`：条带虚拟窗口每批条数 */
const STRIP_PAGE_SIZE = 12;

const stripRowFromEpisode = (e: WorkspaceEpisode): PremiumVideoFile => ({
  uid: `waiting-${e.id}`,
  name: e.playRow.name || e.displayName,
  size: 0,
  type: "video/mp4",
  status: "done",
  progress: 100,
  resourceId: e.id,
  episode: e,
});

/**
 * 将接口列表合并进单条 videoFiles（与旧版 Index：接口顺序为主序，尾部挂仍在传/未入库的本地行）。
 * 全程派生、无 stripSlots；合并时必须保留「已入库但仍带 file 预览」的行的 uid，否则会退回 stripRowFromEpisode 的 waiting-*，React key 变导致条带闪动。
 */
const mergeEpisodesIntoVideoFiles = (prev: PremiumVideoFile[], api: WorkspaceEpisode[]): PremiumVideoFile[] => {
  const byId = new Map(api.map((e) => [e.id, e]));

  const locals = prev.filter(
    (v) =>
      Boolean(v.file) &&
      (v.status === "uploading" ||
        v.status === "queued" ||
        v.status === "error" ||
        (v.status === "uploaded" && !v.episode) ||
        /** 已并表仍持有本地 File/Blob 预览，必须留在管线内；漏掉则下次 list 会新建 waiting-*、uid 变 */
        (v.status === "done" && Boolean(v.episode)))
  );

  const mergedLocals = locals.map((vf) => {
    if (vf.resourceId != null && byId.has(vf.resourceId)) {
      const ep = byId.get(vf.resourceId)!;
      return { ...vf, episode: ep, status: "done" as const };
    }
    return vf;
  });

  const serverRows: PremiumVideoFile[] = api.map((e) => {
    const loc = mergedLocals.find((v) => v.resourceId === e.id);
    if (loc) return { ...loc, episode: e, status: "done" as const, resourceId: e.id };
    const orphan = prev.find((v) => !v.file && (v.episode?.id === e.id || v.resourceId === e.id));
    if (orphan) return { ...orphan, episode: e, resourceId: e.id, status: "done" as const };
    return stripRowFromEpisode(e);
  });

  const tailLocals = mergedLocals.filter((v) => !(v.resourceId != null && byId.has(v.resourceId)));
  return [...serverRows, ...tailLocals];
};

const normalizeCoverUrl = (url?: string | null): string => {
  if (!url) return "";
  return processVideoUrl(url);
};

const VIDEO_EXT = [".mp4", ".avi", ".mov", ".mkv", ".wmv", ".flv", ".webm", ".m4v"];

const isVideoFile = (file: File): boolean => {
  const n = file.name.toLowerCase();
  return file.type.startsWith("video/") || VIDEO_EXT.some((ext) => n.endsWith(ext));
};

const isDocScript = (file: File): boolean => {
  const n = file.name.toLowerCase();
  return n.endsWith(".doc") || n.endsWith(".docx");
};

const isSrtFile = (file: File): boolean => file.name.toLowerCase().endsWith(".srt");

const IconVideo: FC<{ style?: React.CSSProperties }> = ({ style }) => (
  <svg
    style={style}
    width={32}
    height={32}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <polygon points="23 7 16 12 23 17 23 7" />
    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
  </svg>
);

const IconFileText: FC<{ size?: number }> = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const IconSubtitles: FC<{ size?: number }> = ({ size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M7 13h4" />
    <path d="M15 13h2" />
    <path d="M7 9h2" />
    <path d="M13 9h4" />
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10Z" />
  </svg>
);

const IconSparkles: FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
    <path d="m12 3-1.9 5.8-6.1 2 5 3.6-1.9 5.8 5-3.6 5 3.6-1.9-5.8 5-3.6-6.1-2L12 3Z" />
    <path d="M5 3v4" />
    <path d="M19 17v4" />
    <path d="M3 5h4" />
    <path d="M17 19h4" />
  </svg>
);

const IconInfo: FC<{ size?: number; className?: string }> = ({ size = 12, className }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    aria-hidden
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const IconLightbulb: FC<{ size?: number }> = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
    <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
    <path d="M9 18h6" />
    <path d="M10 22h4" />
  </svg>
);

const IconMaximize: FC<{ size?: number }> = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
    <polyline points="15 3 21 3 21 9" />
    <polyline points="9 21 3 21 3 15" />
    <line x1="21" y1="3" x2="14" y2="10" />
    <line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);

const IconCheck: FC<{ size?: number }> = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export const PremiumUploadVideoWorkspacePage: FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { alertConfig, hideAlert, showSuccess, showError, showWarning, showInfo } = useAlert();
  const hasPoints = useHasPermission(PERMISSIONS.POINTS_VIEW) === true;
  const { checkSeriesButtonPermission } = useSeriesButtonPermissions();

  const seriesId = Number(searchParams.get("seriesId") || 0);
  const langCodeParam = searchParams.get("langCode");
  const isSourceList = langCodeParam === null;
  const fromQuick = searchParams.get("from") === "quick";

  const [videoFiles, setVideoFiles] = useState<PremiumVideoFile[]>([]);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(-1);
  const [videoBatchActive, setVideoBatchActive] = useState(false);
  const [subtitleMode, setSubtitleMode] = useState<SubtitleMode>("manual");
  const [listLoading, setListLoading] = useState(false);
  /** 静默刷新列表（不点亮 listLoading）时仍短暂锁住「处理」与拖放，避免与 setVideoFiles 竞态 */
  const [listSyncBusy, setListSyncBusy] = useState(false);
  /** 与旧版 Index `video-preview-grid`：`slice(visibleRange)` +「点击加载剩余」 */
  const [stripVisibleRange, setStripVisibleRange] = useState({ start: 0, end: STRIP_PAGE_SIZE });
  const [uploadBusy, setUploadBusy] = useState(false);
  const [processBusy, setProcessBusy] = useState(false);
  /** POST /video/task/point-preview；未拉取或失败时为 null */
  const [estimatedPoints, setEstimatedPoints] = useState<number | null>(null);
  const [previewTime, setPreviewTime] = useState({ cur: 0, total: 0 });
  const [previewPlaying, setPreviewPlaying] = useState(false);

  const videoInputRef = useRef<HTMLInputElement>(null);
  const scriptInputRef = useRef<HTMLInputElement>(null);
  const subtitleInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const previewShellRef = useRef<HTMLDivElement>(null);
  const progressTrackRef = useRef<HTMLDivElement>(null);
  const fastUploadBatchNoRef = useRef<string | undefined>(undefined);
  const videoFilesRef = useRef<PremiumVideoFile[]>([]);
  /** 与旧版 Index：getSeriesListApi 解析出的剧集名，用于剧本文件名校验 */
  const [workspaceSeriesName, setWorkspaceSeriesName] = useState("");
  /** 与旧版 Index scriptFiles：同名检测、上传态（右侧可不展示列表） */
  const [scriptPanelFiles, setScriptPanelFiles] = useState<ScriptPanelFile[]>([]);
  /** 与旧版 Index uploadingFiles：同一 script-${name}-${size} 不重复提交 */
  const uploadingScriptKeysRef = useRef<Set<string>>(new Set());
  /** 用户手动取消剧本上传时置位，避免 upload catch 与 remove 各弹一次「已取消」 */
  const scriptUploadCancelledUidRef = useRef<string | null>(null);

  const [subtitlePanelFiles, setSubtitlePanelFiles] = useState<SubtitlePanelFile[]>([]);
  const uploadingSubtitleKeysRef = useRef<Set<string>>(new Set());
  const subtitleUploadCancelledUidRef = useRef<string | null>(null);
  const [subtitleMatchOpen, setSubtitleMatchOpen] = useState(false);
  const [subtitleMatchInfo, setSubtitleMatchInfo] = useState<SubtitleMatchInfo | null>(null);
  const [subtitleSwitchOpen, setSubtitleSwitchOpen] = useState(false);
  const [pendingSubtitleMode, setPendingSubtitleMode] = useState<SubtitleMode | null>(null);
  const [autoSubtitleModalOpen, setAutoSubtitleModalOpen] = useState(false);
  const [autoSubtitleModalKey, setAutoSubtitleModalKey] = useState(0);
  const autoSubtitleModalRef = useRef<PremiumAutoSubtitleModalHandle>(null);
  /** 与 Index 卡片内 `subtitle-auto-panel` 同步：来自弹窗内当前选择 */
  const [autoSubtitleParamPreview, setAutoSubtitleParamPreview] = useState<PremiumAutoSubtitleParamsPreview | null>(
    null
  );

  const canUpload = checkSeriesButtonPermission("video:clean:upload");
  const canDelete = checkSeriesButtonPermission("video:clean:delete");

  /**
   * 左侧条带「上传进度」：与列表中的视频条数一致。
   * - total：当前条带全部条目（服务端等待列表 + 本地上传中的行）。
   * - done：服务端已入库条目（有 episode）计为已完成；纯本地上传行以 status===uploaded 为完成。
   */
  const stripUploadStats = useMemo(() => {
    const total = videoFiles.length;
    const done = videoFiles.filter((vf) => {
      if (vf.episode) return true;
      return Boolean(vf.file) && vf.status === "uploaded";
    }).length;
    return { total, done };
  }, [videoFiles]);

  useEffect(() => {
    videoFilesRef.current = videoFiles;
  }, [videoFiles]);

  useEffect(() => {
    setVideoFiles([]);
    setSelectedVideoIndex(-1);
    setScriptPanelFiles([]);
    setSubtitlePanelFiles([]);
    setSubtitleMatchOpen(false);
    setSubtitleMatchInfo(null);
    setSubtitleSwitchOpen(false);
    setPendingSubtitleMode(null);
    setAutoSubtitleModalOpen(false);
    setAutoSubtitleModalKey((k) => k + 1);
    setAutoSubtitleParamPreview(null);
    setEstimatedPoints(null);
  }, [seriesId]);

  const refreshEstimatedPoints = useCallback(async () => {
    if (!seriesId || Number.isNaN(seriesId)) return;
    try {
      const res = await postTaskPointPreviewApi({ seriesId });
      if (res?.code === 0 && res.data != null && typeof res.data.estimatedPoint === "number") {
        setEstimatedPoints(res.data.estimatedPoint);
      }
    } catch {
      /* 静默失败，保留上次展示 */
    }
  }, [seriesId]);

  useEffect(() => {
    let cancelled = false;
    const loadSeriesLabel = async () => {
      if (!seriesId || Number.isNaN(seriesId)) {
        if (!cancelled) setWorkspaceSeriesName("未选择剧集");
        return;
      }
      try {
        const response = await getSeriesListApi({ pageNo: 1, pageSize: 1000 });
        if (cancelled) return;
        const list = response.data?.list || [];
        const target = list.find(
          (s) => (s.id != null && s.id === seriesId) || (s.seriesId != null && s.seriesId === seriesId)
        );
        setWorkspaceSeriesName(target?.name || "未知剧集");
      } catch {
        if (!cancelled) setWorkspaceSeriesName("获取失败");
      }
    };
    void loadSeriesLabel();
    return () => {
      cancelled = true;
    };
  }, [seriesId]);

  const episodes = useMemo(
    () => videoFiles.map((v) => v.episode).filter((e): e is WorkspaceEpisode => Boolean(e)),
    [videoFiles]
  );

  const selectedStripFile = useMemo(
    () => (selectedVideoIndex >= 0 ? videoFiles[selectedVideoIndex] ?? null : null),
    [videoFiles, selectedVideoIndex]
  );

  const selectedId = selectedStripFile?.episode?.id ?? selectedStripFile?.resourceId ?? null;

  useEffect(() => {
    if (fromQuick && subtitleMode === "auto") {
      setSubtitleMode("manual");
    }
  }, [fromQuick, subtitleMode]);

  const backToVideoList = useMemo(() => {
    const q = new URLSearchParams();
    const sid = searchParams.get("seriesId");
    const seriesName = searchParams.get("seriesName");
    if (sid) q.set("seriesId", sid);
    if (seriesName) q.set("seriesName", seriesName);
    const langCode = searchParams.get("langCode");
    if (langCode) q.set("langCode", langCode);
    if (searchParams.get("isPure") === "true") q.set("isPure", "true");
    if (searchParams.get("from") === "quick") q.set("from", "quick");
    const query = q.toString();
    return query ? `${PREMIUM_VIDEO_LIST_REFACTOR_BASE}?${query}` : PREMIUM_VIDEO_LIST_REFACTOR_BASE;
  }, [searchParams]);

  const selectedEpisode = selectedStripFile?.episode ?? null;

  const hasManualSubtitleData = subtitlePanelFiles.length > 0;
  const hasAutoSubtitleData = useMemo(
    () =>
      videoFiles.some(
        (v) =>
          Boolean(v.subtitleExtractType) || (Array.isArray(v.ocrBbox) && (v.ocrBbox?.length ?? 0) === 4)
      ),
    [videoFiles]
  );

  const handleAutoSubtitleParamsPreviewChange = useCallback((p: PremiumAutoSubtitleParamsPreview) => {
    setAutoSubtitleParamPreview(p);
  }, []);

  const autoSubtitlePanelLabels = useMemo(() => {
    const pv = autoSubtitleParamPreview ?? { extract: "vision" as const, region: "middle" as const };
    const extractLabel = pv.extract === "vision" ? "画面识别" : "音频识别";
    const regionLabel =
      pv.extract === "audio"
        ? "不适用"
        : pv.region === "custom"
          ? "自定义"
          : "预设区域：画面中部靠下";
    return { extractLabel, regionLabel };
  }, [autoSubtitleParamPreview]);

  const selectedPending = useMemo(() => {
    const vf = selectedStripFile;
    if (!vf || vf.episode) return null;
    return vf;
  }, [selectedStripFile]);

  const videoSlotUploading = useMemo(
    () =>
      videoBatchActive ||
      videoFiles.some((v) => v.status === "uploading" || v.status === "queued"),
    [videoBatchActive, videoFiles]
  );

  const playUrl = useMemo(() => {
    const vf = selectedStripFile;
    if (!vf) return "";
    if (vf.episode) return getVideoPlayUrl(vf.episode.playRow);
    return vf.previewUrl || "";
  }, [selectedStripFile]);

  const loadEpisodes = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = Boolean(opts?.silent);
    if (!seriesId || Number.isNaN(seriesId)) {
      setVideoFiles([]);
      return;
    }
    if (!silent) {
      setListLoading(true);
    } else {
      setListSyncBusy(true);
    }
    try {
      if (fromQuick) {
        const lang = (langCodeParam || "").trim();
        if (!lang) {
          showError("缺少语种参数");
          setVideoFiles([]);
          return;
        }
        const ftRes = await getFastTranslateSeriesItemListApi({
          seriesId,
          targetLangCode: lang,
          sort: 2,
        });
        if (ftRes.code !== 0 || !Array.isArray(ftRes.data)) {
          showError(ftRes.msg || "获取视频列表失败");
          setVideoFiles([]);
          return;
        }
        const rows = mapFastTranslateSeriesItemListToTaskItems(ftRes.data);
        const nextEpisodes: WorkspaceEpisode[] = rows.map((row) => {
          const tag = Number(row.tag) || 0;
          const name = row.name || "";
          return {
            id: row.id,
            displayName: baseName(name),
            durationLabel: formatDuration((row as { duration?: number }).duration),
            durationSeconds: Number((row as { duration?: number }).duration || 0),
            coverUrl: normalizeCoverUrl((row as { cover_url?: string }).cover_url),
            statusCode: row.status,
            playRow: {
              id: row.id,
              name,
              status: row.status,
              create_time: row.create_time || "",
              descript: row.descript,
              result_url: row.result_url,
              pure_video_url: row.pure_video_url,
              file_url: row.file_url,
              tag,
            },
          };
        });
        setVideoFiles((prev) => mergeEpisodesIntoVideoFiles(prev, nextEpisodes));
      } else if (isSourceList) {
        const res = await getPureVideoListApi(seriesId, 2);
        if (res.code !== 0 || !res.data) {
          showError(res.msg || "获取原剧视频列表失败");
          setVideoFiles([]);
          return;
        }
        /** 与旧版 Index `fetchWaitingVideos`：仅展示 status===0「等待处理」 */
        const waitingPure = res.data.filter((item) => item.status === 0);
        const nextEpisodes: WorkspaceEpisode[] = waitingPure.map((item) => {
          const name = item.name.replace(/\.[^/.]+$/, "");
          const tag = Number(item.tag) || 0;
          return {
            id: item.id,
            displayName: name,
            durationLabel: formatDuration(item.duration),
            durationSeconds: Number(item.duration ?? 0),
            coverUrl: normalizeCoverUrl(item.coverUrl),
            statusCode: item.status,
            playRow: {
              id: item.id,
              name,
              status: item.status,
              create_time: item.createTime || "",
              descript: item.description ?? null,
              result_url: item.resultUrl ?? null,
              pure_video_url: item.pureVideoUrl ?? null,
              file_url: item.fileUrl ?? null,
              tag,
            },
          };
        });
        setVideoFiles((prev) => mergeEpisodesIntoVideoFiles(prev, nextEpisodes));
      } else {
        /** 与旧版 Index `fetchWaitingVideos`：多语种等待列表固定 `langCode: 'en'` */
        const data = await getTaskListApi({
          seriesId,
          langCode: "en",
          sort: 2,
        });
        /** 与旧版 Index `fetchWaitingVideos`：仅展示 status===0「等待处理」 */
        const list = (data.data || []).filter((item) => (item as Task.TaskItem).status === 0);
        const nextEpisodes: WorkspaceEpisode[] = list.map((item) => {
          const row = item as Task.TaskItem;
          const tag = Number(row.tag) || 0;
          const rawName = row.name || "";
          const displayName = baseName(rawName);
          return {
            id: row.id,
            displayName,
            durationLabel: formatDuration((row as { duration?: number }).duration),
            durationSeconds: Number((row as { duration?: number }).duration || 0),
            coverUrl: normalizeCoverUrl((row as { cover_url?: string }).cover_url),
            statusCode: row.status,
            playRow: {
              id: row.id,
              name: rawName,
              status: row.status,
              create_time: row.create_time || "",
              descript: row.descript,
              result_url: row.result_url,
              pure_video_url: row.pure_video_url,
              file_url: row.file_url,
              tag,
            },
          };
        });
        setVideoFiles((prev) => mergeEpisodesIntoVideoFiles(prev, nextEpisodes));
      }
    } catch (e) {
      console.error(e);
      showError("获取视频列表失败");
      setVideoFiles([]);
    } finally {
      if (!silent) {
        setListLoading(false);
      } else {
        setListSyncBusy(false);
      }
      /* 首次进入 / 每次列表同步结束后拉一次预估积分（与上传、删除后刷新一致） */
      void refreshEstimatedPoints();
    }
  }, [seriesId, fromQuick, langCodeParam, isSourceList, showError, refreshEstimatedPoints]);

  useEffect(() => {
    void loadEpisodes();
  }, [loadEpisodes]);

  useEffect(() => {
    if (videoFiles.length > 0) {
      setStripVisibleRange({ start: 0, end: Math.min(STRIP_PAGE_SIZE, videoFiles.length) });
    } else {
      setStripVisibleRange({ start: 0, end: STRIP_PAGE_SIZE });
    }
  }, [videoFiles.length]);

  /** 与旧版 Index：有视频时默认选中第一项 */
  useEffect(() => {
    if (videoFiles.length > 0 && selectedVideoIndex === -1) {
      setSelectedVideoIndex(0);
    }
  }, [videoFiles.length, selectedVideoIndex]);

  useEffect(() => {
    if (videoFiles.length === 0) {
      setSelectedVideoIndex(-1);
      return;
    }
    if (selectedVideoIndex >= videoFiles.length) {
      setSelectedVideoIndex(videoFiles.length - 1);
    }
  }, [videoFiles.length, selectedVideoIndex]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    v.currentTime = 0;
    setPreviewTime({ cur: 0, total: 0 });
    setPreviewPlaying(false);
  }, [playUrl]);

  const openVideoPicker = () => {
    if (!canUpload) {
      showWarning("暂无上传权限");
      return;
    }
    if (!seriesId) {
      showWarning("缺少剧集ID");
      return;
    }
    if (videoSlotUploading) {
      showWarning("正在上传视频，请稍候");
      return;
    }
    videoInputRef.current?.click();
  };

  const removeStripVideoByUid = useCallback((uid: string) => {
    let removedIndex = -1;
    setVideoFiles((prev) => {
      removedIndex = prev.findIndex((v) => v.uid === uid);
      const item = removedIndex >= 0 ? prev[removedIndex] : undefined;
      if (item) {
        try {
          item.abortController?.abort();
        } catch {
          /* ignore */
        }
        try {
          if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
        } catch {
          /* ignore */
        }
        if (item.thumbnailUrl) {
          try {
            URL.revokeObjectURL(item.thumbnailUrl);
          } catch {
            /* ignore */
          }
        }
      }
      const next = prev.filter((v) => v.uid !== uid);
      videoFilesRef.current = next;
      return next;
    });
    setSelectedVideoIndex((cur) => {
      const len = videoFilesRef.current.length;
      if (removedIndex < 0) return len > 0 ? Math.min(cur, len - 1) : -1;
      if (cur === removedIndex) return len > 0 ? Math.min(removedIndex, len - 1) : -1;
      if (cur > removedIndex) return cur - 1;
      return cur;
    });
  }, []);

  const handleRemovePendingStrip = (e: React.MouseEvent, uid: string) => {
    e.stopPropagation();
    removeStripVideoByUid(uid);
  };

  const isAbortUploadError = (e: unknown): boolean => {
    if (e instanceof DOMException && e.name === "AbortError") return true;
    if (e instanceof Error && e.name === "AbortError") return true;
    const msg = typeof e === "object" && e !== null && "message" in e ? String((e as Error).message) : "";
    return msg.includes("aborted") || msg === "上传已取消";
  };

  const handleVideoFiles = async (files: FileList | File[]) => {
    if (!canUpload) {
      showWarning("暂无上传权限");
      return;
    }
    const arr = Array.from(files).filter(isVideoFile);
    if (arr.length === 0) {
      showWarning("请选择视频文件");
      return;
    }

    const validFiles: File[] = [];
    for (const file of arr) {
      const nameCheck = validateVideoUploadFileName(file.name);
      if (!nameCheck.valid) {
        showError(nameCheck.message || getVideoUploadFileNameInvalidMessage(baseName(file.name)));
        continue;
      }
      validFiles.push(file);
    }
    if (validFiles.length === 0) {
      return;
    }
    if (!seriesId) {
      showWarning("缺少剧集ID");
      return;
    }
    if (videoBatchActive) {
      showWarning("正在处理上一批上传，请稍候");
      return;
    }

    const room = MAX_UPLOAD_VIDEO_EPISODES - videoFiles.length;
    if (room <= 0) {
      showWarning(`视频最多上传 ${MAX_UPLOAD_VIDEO_EPISODES} 集`);
      return;
    }
    const batch = validFiles.slice(0, room);
    if (validFiles.length > batch.length) {
      showWarning(`单次最多还能添加 ${room} 集，已为您截取前面的文件`);
    }

    const baseTime = Date.now();
    const itemsWithQueue: PremiumVideoFile[] = batch.map((file, i) => {
      const uid = `${baseTime}-${i}-${Math.random().toString(16).slice(2)}`;
      const status: PremiumVideoStripStatus = fromQuick ? (i === 0 ? "uploading" : "queued") : "uploading";
      return {
        uid,
        file,
        name: file.name,
        size: file.size,
        type: file.type || "video/mp4",
        previewUrl: URL.createObjectURL(file),
        status,
        progress: 0,
        abortController: new AbortController(),
      };
    });

    let firstNewIndex = 0;
    flushSync(() => {
      setVideoFiles((prev) => {
        firstNewIndex = prev.length;
        const next = [...prev, ...itemsWithQueue];
        videoFilesRef.current = next;
        return next;
      });
    });
    setSelectedVideoIndex(firstNewIndex);
    setVideoBatchActive(true);
    fastUploadBatchNoRef.current = undefined;

    void Promise.all(
      itemsWithQueue.map(async (entry) => {
        try {
          if (!entry.abortController) return;
          if (entry.abortController.signal.aborted) return;
          const meta = await getVideoMetadata(entry.file!);
          if (entry.abortController.signal.aborted) return;
          const thumbUrl = URL.createObjectURL(meta.coverBlob);
          setVideoFiles((prev) =>
            prev.some((p) => p.uid === entry.uid)
              ? prev.map((p) =>
                  p.uid === entry.uid
                    ? { ...p, thumbnailUrl: thumbUrl, durationSeconds: meta.duration }
                    : p
                )
              : prev
          );
        } catch {
          /* 封面失败不影响列表展示 */
        }
      })
    );

    let successCount = 0;

    const revokeAndRemoveStripRow = (uid: string) => {
      removeStripVideoByUid(uid);
    };

    try {
      if (fromQuick) {
        for (const entry of itemsWithQueue) {
          if (!videoFilesRef.current.some((p) => p.uid === entry.uid)) continue;

          setVideoFiles((prev) =>
            prev.map((p) =>
              p.uid === entry.uid && p.status === "queued"
                ? { ...p, status: "uploading" as const, progress: 0 }
                : p
            )
          );

          try {
            const ftInfo = await uploadFastTranslateWorkspaceVideo(
              fastUploadBatchNoRef,
              entry.file!,
              (p) => {
                setVideoFiles((prev) =>
                  prev.map((x) => (x.uid === entry.uid ? { ...x, progress: p } : x))
                );
              },
              entry.abortController!.signal
            );
            successCount += 1;
            setVideoFiles((prev) => {
              const next = prev.map((x) =>
                x.uid === entry.uid
                  ? {
                      ...x,
                      status: "uploaded" as const,
                      progress: 100,
                      remoteCoverUrl: ftInfo.coverUrl || undefined,
                      uploadedMatchKey: baseName(ftInfo.episodeName),
                      uploadedSourceFileKey: baseName(entry.name),
                      durationSeconds: ftInfo.duration,
                    }
                  : x
              );
              videoFilesRef.current = next;
              return next;
            });
            void refreshEstimatedPoints();
          } catch (e: unknown) {
            if (entry.abortController!.signal.aborted || isAbortUploadError(e)) {
              revokeAndRemoveStripRow(entry.uid);
              continue;
            }
            const msg = e instanceof Error ? e.message : "上传失败";
            setVideoFiles((prev) =>
              prev.map((x) =>
                x.uid === entry.uid
                  ? { ...x, status: "error" as const, errorMessage: msg, progress: 0 }
                  : x
              )
            );
            showError(`${entry.name}：${msg}`);
          }
        }
      } else {
        const results = await Promise.allSettled(
          itemsWithQueue.map(async (entry) => {
            if (!videoFilesRef.current.some((p) => p.uid === entry.uid)) return false;
            try {
              const taskId = await uploadSingleFileToTos(
                seriesId,
                entry.file!,
                "video",
                (p) => {
                  setVideoFiles((prev) =>
                    prev.map((x) => (x.uid === entry.uid ? { ...x, progress: p } : x))
                  );
                },
                entry.abortController!.signal
              );
              setVideoFiles((prev) => {
                const next = prev.map((x) =>
                  x.uid === entry.uid
                    ? {
                        ...x,
                        status: "uploaded" as const,
                        progress: 100,
                        resourceId: taskId,
                      }
                    : x
                );
                videoFilesRef.current = next;
                return next;
              });
              void refreshEstimatedPoints();
              return true;
            } catch (e: unknown) {
              if (entry.abortController!.signal.aborted || isAbortUploadError(e)) {
                revokeAndRemoveStripRow(entry.uid);
                return false;
              }
              const msg = e instanceof Error ? e.message : "上传失败";
              setVideoFiles((prev) =>
                prev.map((x) =>
                  x.uid === entry.uid
                    ? { ...x, status: "error" as const, errorMessage: msg, progress: 0 }
                    : x
                )
              );
              showError(`${entry.name}：${msg}`);
              return false;
            }
          })
        );
        successCount = results.filter((r) => r.status === "fulfilled" && r.value === true).length;
      }

      /** 与旧版 Index：上传完成后就地更新 videoFiles，不自动重拉「等待处理」列表 */
      if (successCount > 0) {
        showSuccess(
          successCount === batch.length ? "视频上传完成" : `已上传 ${successCount}/${batch.length} 个视频`
        );
      }
    } finally {
      setVideoBatchActive(false);
    }
  };

  /** 与旧版 Index `handleScriptFileUpload` + `uploadScriptToBackend`：单文件、防重复键、列表回显、可取消上传 */
  const uploadOneScriptFile = useCallback(
    async (file: File) => {
      const key = `script-${file.name}-${file.size}`;
      if (uploadingScriptKeysRef.current.has(key)) {
        showWarning("剧本文件正在上传中，跳过重复上传");
        return;
      }
      uploadingScriptKeysRef.current.add(key);
      const uid = `${Date.now()}-${Math.random()}`;
      const abortController = new AbortController();
      const newRow: ScriptPanelFile = {
        uid,
        name: file.name,
        size: file.size,
        type: file.type || "application/octet-stream",
        status: "uploading",
        file,
        abortController,
      };
      setScriptPanelFiles((prev) => [...prev, newRow]);
      try {
        const taskId = await uploadSingleFileToTos(seriesId, file, "script", undefined, abortController.signal);
        setScriptPanelFiles((prev) =>
          prev.map((sf) =>
            sf.uid === uid
              ? { ...sf, status: "done" as const, resourceId: taskId, abortController: undefined }
              : sf
          )
        );
        showSuccess(`${file.name} 上传成功`);
        if (
          workspaceSeriesName &&
          workspaceSeriesName !== "未知剧集" &&
          workspaceSeriesName !== "未选择剧集" &&
          workspaceSeriesName !== "获取失败"
        ) {
          const scriptCheck = checkScriptFileNameAgainstSeries(file.name, workspaceSeriesName);
          if (!scriptCheck.isValid) {
            Modal.warning({ title: "提醒", content: scriptCheck.message });
          }
        }
      } catch (e: unknown) {
        if (isAbortUploadError(e)) {
          const userClickedRemove = scriptUploadCancelledUidRef.current === uid;
          scriptUploadCancelledUidRef.current = null;
          setScriptPanelFiles((prev) => prev.filter((sf) => sf.uid !== uid));
          if (userClickedRemove) {
            showInfo("已取消剧本上传任务");
          }
        } else {
          const msg = e instanceof Error ? e.message : "";
          setScriptPanelFiles((prev) =>
            prev.map((sf) => (sf.uid === uid ? { ...sf, status: "error" as const, abortController: undefined } : sf))
          );
          showError(`${file.name} 上传失败${msg ? `：${msg}` : ""}`);
        }
      } finally {
        uploadingScriptKeysRef.current.delete(key);
      }
    },
    [seriesId, workspaceSeriesName, showSuccess, showError, showWarning, showInfo]
  );

  /** 与旧版 Index `handleRemoveScript`：上传中取消、已上传调删除接口 */
  const handleRemoveScriptPanelRow = async (e: React.MouseEvent, uid: string) => {
    e.stopPropagation();
    const row = scriptPanelFiles.find((x) => x.uid === uid);
    if (!row) return;
    if (row.status === "uploading") {
      scriptUploadCancelledUidRef.current = uid;
      row.abortController?.abort();
      setScriptPanelFiles((prev) => prev.filter((x) => x.uid !== uid));
      showInfo("已取消剧本上传任务");
      return;
    }
    if (row.status === "done" && row.resourceId != null) {
      if (!canDelete) {
        showWarning("暂无删除权限");
        return;
      }
      try {
        if (fromQuick) {
          const res = await deleteFastTranslateSeriesItemApi({ seriesId, itemId: row.resourceId });
          if (!(res?.code === 0 && res.data)) {
            showError(res?.msg || "删除失败");
            return;
          }
        } else {
          const res = await delTaskApi(row.resourceId);
          if (res.code !== 0) {
            showError(res.msg || "删除失败");
            return;
          }
        }
        showSuccess("已删除");
        setScriptPanelFiles((prev) => prev.filter((x) => x.uid !== uid));
      } catch {
        showError("删除失败");
      }
      return;
    }
    setScriptPanelFiles((prev) => prev.filter((x) => x.uid !== uid));
  };

  const handleScriptFiles = async (files: FileList | File[]) => {
    if (fromQuick) {
      showWarning("AI速译剧集请在工作台内使用速译流程上传剧本");
      return;
    }
    const raw = Array.from(files).filter(isDocScript);
    if (raw.length === 0) {
      showError("剧本文件只支持 doc 和 docx 格式，请重新选择文件");
      return;
    }
    if (raw.length > 1) {
      showWarning("仅支持上传 1 个剧本文件，已为您保留第一个");
    }
    const arr = raw.slice(0, 1);
    if (!seriesId || Number.isNaN(seriesId)) {
      showError("请先选择剧集文件夹");
      return;
    }
    if (!canUpload) {
      showWarning("暂无上传权限");
      return;
    }

    if (scriptPanelFiles.length > 0) {
      flushSync(() => {
        setScriptPanelFiles((prev) => {
          prev.forEach((s) => s.abortController?.abort());
          return [];
        });
      });
    }

    setUploadBusy(true);
    try {
      for (const file of arr) {
        await uploadOneScriptFile(file);
      }
    } finally {
      setUploadBusy(false);
    }
  };

  const subtitleMatchesStripVideo = useCallback(
    (file: File) => videoFiles.some((v) => baseName(v.name) === baseName(file.name)),
    [videoFiles]
  );

  const applySubtitleModeSwitch = useCallback((target: SubtitleMode) => {
    if (target === "auto") {
      setSubtitlePanelFiles([]);
      setAutoSubtitleParamPreview({ extract: "vision", region: "middle" });
      setAutoSubtitleModalKey((k) => k + 1);
      setSubtitleMode("auto");
      setAutoSubtitleModalOpen(true);
      return;
    }
    setAutoSubtitleModalOpen(false);
    setAutoSubtitleModalKey((k) => k + 1);
    setAutoSubtitleParamPreview(null);
    setSubtitleMode("manual");
  }, []);

  const handleSubtitleModeSwitchClick = useCallback(
    (target: SubtitleMode) => {
      if (fromQuick) return;
      if (target === subtitleMode) {
        if (target === "auto") {
          if (videoFiles.length === 0) {
            showError("请上传视频后选择字幕类型");
            showWarning("请上传视频后选择字幕类型");
            return;
          }
          setAutoSubtitleModalOpen(true);
        }
        return;
      }
      if (target === "auto" && videoFiles.length === 0) {
        showError("请上传视频后选择字幕类型");
        showWarning("请上传视频后选择字幕类型");
        return;
      }
      const hasCurrent = subtitleMode === "manual" ? hasManualSubtitleData : hasAutoSubtitleData;
      if (!hasCurrent) {
        applySubtitleModeSwitch(target);
        return;
      }
      setPendingSubtitleMode(target);
      setSubtitleSwitchOpen(true);
    },
    [
      fromQuick,
      subtitleMode,
      videoFiles.length,
      hasManualSubtitleData,
      hasAutoSubtitleData,
      applySubtitleModeSwitch,
      showError,
      showWarning,
    ]
  );

  const handleSubtitleSwitchConfirm = useCallback(() => {
    if (!pendingSubtitleMode) {
      setSubtitleSwitchOpen(false);
      return;
    }
    applySubtitleModeSwitch(pendingSubtitleMode);
    setSubtitleSwitchOpen(false);
    setPendingSubtitleMode(null);
  }, [pendingSubtitleMode, applySubtitleModeSwitch]);

  const handleSubtitleSwitchCancel = useCallback(() => {
    setSubtitleSwitchOpen(false);
    setPendingSubtitleMode(null);
  }, []);

  const uploadOneSubtitleFile = useCallback(
    async (file: File) => {
      const key = `subtitle-${file.name}-${file.size}`;
      if (uploadingSubtitleKeysRef.current.has(key)) {
        showWarning("字幕文件正在上传中，跳过重复上传");
        return;
      }
      uploadingSubtitleKeysRef.current.add(key);
      const uid = `${Date.now()}-${Math.random()}`;
      const abortController = new AbortController();
      const newRow: SubtitlePanelFile = {
        uid,
        name: file.name,
        size: file.size,
        type: file.type || "text/plain",
        status: "uploading",
        file,
        abortController,
      };
      setSubtitlePanelFiles((prev) => [...prev, newRow]);
      try {
        const taskId = await uploadSingleFileToTos(seriesId, file, "subtitle", undefined, abortController.signal);
        setSubtitlePanelFiles((prev) =>
          prev.map((sf) => (sf.uid === uid ? { ...sf, status: "done" as const, resourceId: taskId } : sf))
        );
        showSuccess(`${file.name} 上传成功`);
      } catch (e: unknown) {
        if (isAbortUploadError(e)) {
          const userClickedRemove = subtitleUploadCancelledUidRef.current === uid;
          subtitleUploadCancelledUidRef.current = null;
          setSubtitlePanelFiles((prev) => prev.filter((sf) => sf.uid !== uid));
          if (userClickedRemove) {
            showInfo("已取消字幕上传任务");
          }
        } else {
          setSubtitlePanelFiles((prev) => prev.filter((sf) => sf.uid !== uid));
          showError(`${file.name} 上传失败`);
        }
      } finally {
        uploadingSubtitleKeysRef.current.delete(key);
      }
    },
    [seriesId, showSuccess, showError, showWarning, showInfo]
  );

  const handleSubtitleMatchConfirm = useCallback(async () => {
    if (!subtitleMatchInfo) return;
    const { invalidFiles, allFiles } = subtitleMatchInfo;
    const batchBase = new Set(allFiles.map((f) => baseName(f.name)));
    setSubtitlePanelFiles((prev) => prev.filter((sf) => !batchBase.has(baseName(sf.name))));
    setSubtitleMatchOpen(false);
    setSubtitleMatchInfo(null);
    const validFiles = allFiles.filter((file) => !invalidFiles.some((inv) => inv.name === baseName(file.name)));
    if (validFiles.length === 0) return;
    if (!seriesId || !canUpload) return;
    setUploadBusy(true);
    try {
      for (const file of validFiles) {
        await uploadOneSubtitleFile(file);
      }
    } finally {
      setUploadBusy(false);
    }
  }, [subtitleMatchInfo, seriesId, canUpload, uploadOneSubtitleFile]);

  const handleSubtitleMatchCancel = useCallback(() => {
    if (!subtitleMatchInfo) return;
    const { allFiles } = subtitleMatchInfo;
    const names = new Set(allFiles.map((f) => baseName(f.name)));
    setSubtitlePanelFiles((prev) => prev.filter((sf) => !names.has(baseName(sf.name))));
    setSubtitleMatchOpen(false);
    setSubtitleMatchInfo(null);
  }, [subtitleMatchInfo]);

  const handleRemoveSubtitlePanelRow = useCallback(
    async (e: React.MouseEvent, uid: string) => {
      e.stopPropagation();
      const row = subtitlePanelFiles.find((x) => x.uid === uid);
      if (!row) return;
      if (row.status === "uploading") {
        subtitleUploadCancelledUidRef.current = uid;
        row.abortController?.abort();
        setSubtitlePanelFiles((prev) => prev.filter((x) => x.uid !== uid));
        showInfo("已取消字幕上传任务");
        return;
      }
      if (row.status === "done" && row.resourceId != null) {
        if (!canDelete) {
          showWarning("暂无删除权限");
          return;
        }
        try {
          if (fromQuick) {
            const res = await deleteFastTranslateSeriesItemApi({ seriesId, itemId: row.resourceId });
            if (!(res?.code === 0 && res.data)) {
              showError(res?.msg || "删除失败");
              return;
            }
          } else {
            const res = await delTaskApi(row.resourceId);
            if (res.code !== 0) {
              showError(res.msg || "删除失败");
              return;
            }
          }
          showSuccess("已删除");
          setSubtitlePanelFiles((prev) => prev.filter((x) => x.uid !== uid));
        } catch {
          showError("删除失败");
        }
        return;
      }
      setSubtitlePanelFiles((prev) => prev.filter((x) => x.uid !== uid));
    },
    [subtitlePanelFiles, canDelete, fromQuick, seriesId, showWarning, showError, showSuccess, showInfo]
  );

  const handleSubtitleFiles = async (files: FileList | File[]) => {
    if (subtitleMode === "auto") {
      if (fromQuick) {
        showWarning("AI速译剧集不支持在此页自动识别字幕");
        return;
      }
      if (videoFiles.length === 0) {
        showWarning("请上传视频后选择字幕类型");
        return;
      }
      setAutoSubtitleModalOpen(true);
      return;
    }

    if (fromQuick) {
      showWarning("AI速译剧集暂在此页仅支持视频上传，字幕请使用列表页能力");
      return;
    }

    const arr = Array.from(files).filter(isSrtFile);
    if (arr.length === 0) {
      showError("字幕文件只支持 srt 格式，请重新选择文件");
      return;
    }
    if (!seriesId || Number.isNaN(seriesId)) {
      showError("请先选择剧集文件夹");
      return;
    }
    if (!canUpload) {
      showWarning("暂无上传权限");
      return;
    }

    const invalid = arr.filter((f) => !subtitleMatchesStripVideo(f));
    const valid = arr.filter((f) => subtitleMatchesStripVideo(f));

    if (invalid.length > 0) {
      const newRows: SubtitlePanelFile[] = arr.map((file) => ({
        uid: `${Date.now()}-${Math.random()}-${file.name}`,
        name: file.name,
        size: file.size,
        type: file.type || "text/plain",
        status: "uploading" as const,
        file,
      }));
      setSubtitlePanelFiles((prev) => [...prev, ...newRows]);
      setSubtitleMatchInfo({
        invalidFiles: invalid.map((f) => ({ file: f, name: baseName(f.name) })),
        allFiles: arr,
      });
      setSubtitleMatchOpen(true);
      return;
    }

    setUploadBusy(true);
    try {
      for (const file of valid) {
        await uploadOneSubtitleFile(file);
      }
    } finally {
      setUploadBusy(false);
    }
  };

  const handleDeleteEpisode = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    if (!canDelete) {
      showWarning("暂无删除权限");
      return;
    }
    try {
      if (fromQuick) {
        const res = await deleteFastTranslateSeriesItemApi({ seriesId, itemId: id });
        if (!(res?.code === 0 && res.data)) {
          showError(res?.msg || "删除失败");
          return;
        }
      } else {
        const res = await delTaskApi(id);
        if (res.code !== 0) {
          showError(res.msg || "删除失败");
          return;
        }
      }
      showSuccess("已删除");
      await loadEpisodes();
    } catch {
      showError("删除失败");
    }
  };

  const handleDeleteUploadedPending = async (e: React.MouseEvent, pendingUid: string, serverTaskId: number) => {
    e.stopPropagation();
    if (!canDelete) {
      showWarning("暂无删除权限");
      return;
    }
    try {
      if (fromQuick) {
        const res = await deleteFastTranslateSeriesItemApi({ seriesId, itemId: serverTaskId });
        if (!(res?.code === 0 && res.data)) {
          showError(res?.msg || "删除失败");
          return;
        }
      } else {
        const res = await delTaskApi(serverTaskId);
        if (res.code !== 0) {
          showError(res.msg || "删除失败");
          return;
        }
      }
      showSuccess("已删除");
      removeStripVideoByUid(pendingUid);
      await loadEpisodes();
    } catch {
      showError("删除失败");
    }
  };

  const handleProcess = async () => {
    const processGuardBusy =
      !seriesId || uploadBusy || videoSlotUploading || processBusy || listLoading || listSyncBusy;
    if (processGuardBusy) {
      if (!seriesId) {
        showWarning("缺少剧集ID");
      } else if (uploadBusy || videoSlotUploading) {
        showWarning("请等待上传完成");
      } else if (processBusy) {
        showInfo("正在提交处理，请稍候...");
      } else {
        showInfo("列表同步中，请稍候再试");
      }
      return;
    }

    if (!seriesId) {
      showWarning("缺少剧集ID");
      return;
    }
    if (uploadBusy || videoSlotUploading) {
      showWarning("请等待上传完成");
      return;
    }

    if (subtitleMode === "manual" && subtitlePanelFiles.length === 0) {
      showWarning("请选择字幕识别方式后再进行处理");
      showError("请选择字幕识别方式后再进行处理");
      return;
    }

    if (subtitleMode === "auto") {
      if (fromQuick) {
        showWarning("AI速译剧集不支持在此页自动识别字幕");
        return;
      }
      const autoParams = autoSubtitleModalRef.current?.buildAutoSubtitleParams() ?? null;
      if (!autoParams) {
        showWarning("暂无可自动识别字幕的视频");
        return;
      }
      if (autoSubtitleModalRef.current?.isVideoCustomOcrIncomplete()) {
        setAutoSubtitleModalOpen(true);
        showWarning('识别区域坐标尚未就绪，请先在“自动识别字幕”弹窗中等待并点击确定后再处理');
        return;
      }
      try {
        const autoRes = await autoSubtitleApi(autoParams);
        if (autoRes.code !== 0) {
          showError(autoRes.msg || "自动识别字幕提交失败");
          return;
        }
      } catch (e: unknown) {
        showError(e instanceof Error ? e.message : "自动识别字幕提交失败");
        return;
      }
    }

    setProcessBusy(true);
    try {
      const res = await submitResourceApi(seriesId);
      if (res.code !== 0) {
        showError(res.msg || "提交处理失败");
        return;
      }
      showSuccess("已提交后台处理");
      window.dispatchEvent(new Event("points:refresh"));
      triggerMembershipRefresh();
      navigate(backToVideoList);
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : "提交失败");
    } finally {
      setProcessBusy(false);
    }
  };


  const onPreviewTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    setPreviewTime({ cur: v.currentTime, total: v.duration || 0 });
  };

  const previewProgressPercent =
    previewTime.total > 0 ? Math.min(100, Math.round((previewTime.cur / previewTime.total) * 100)) : 0;

  const previewBarPercent =
    selectedPending?.status === "uploading" ? selectedPending.progress : previewProgressPercent;

  const previewSeekEnabled =
    Boolean(playUrl) && !(selectedPending?.status === "uploading");

  const seekPreviewToClientX = useCallback((clientX: number) => {
    const v = videoRef.current;
    const track = progressTrackRef.current;
    if (!v || !track) return;
    const dur = v.duration;
    if (!Number.isFinite(dur) || dur <= 0) return;
    const rect = track.getBoundingClientRect();
    if (rect.width <= 0) return;
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    v.currentTime = ratio * dur;
    setPreviewTime({ cur: v.currentTime, total: dur });
  }, []);

  const onProgressTrackPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!previewSeekEnabled) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const track = progressTrackRef.current;
    if (track) track.setPointerCapture(e.pointerId);
    seekPreviewToClientX(e.clientX);
  };

  const onProgressTrackPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!previewSeekEnabled) return;
    const track = progressTrackRef.current;
    if (!track?.hasPointerCapture(e.pointerId)) return;
    seekPreviewToClientX(e.clientX);
  };

  const onProgressTrackPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const track = progressTrackRef.current;
    if (track?.hasPointerCapture(e.pointerId)) {
      track.releasePointerCapture(e.pointerId);
    }
  };

  const onProgressTrackKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!previewSeekEnabled) return;
    const v = videoRef.current;
    if (!v) return;
    const dur = v.duration;
    if (!Number.isFinite(dur) || dur <= 0) return;
    const step = Math.max(1, dur / 20);
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      v.currentTime = Math.max(0, v.currentTime - step);
      setPreviewTime({ cur: v.currentTime, total: dur });
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      v.currentTime = Math.min(dur, v.currentTime + step);
      setPreviewTime({ cur: v.currentTime, total: dur });
    }
  };

  const formatClock = (sec: number) => {
    const s = Math.floor(sec % 60);
    const m = Math.floor(sec / 60);
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const handleFullscreen = () => {
    const el = previewShellRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void el.requestFullscreen();
    }
  };

  const onDropOnWorkspace = (ev: React.DragEvent) => {
    ev.preventDefault();
    if (uploadBusy || listLoading || listSyncBusy || videoSlotUploading || videoBatchActive) return;
    if (!canUpload) {
      showWarning("暂无上传权限");
      return;
    }
    if (ev.dataTransfer.files?.length) {
      void handleVideoFiles(ev.dataTransfer.files);
    }
  };

  const togglePreviewPlayPause = () => {
    const v = videoRef.current;
    if (!v || !playUrl) return;
    if (v.paused) {
      void v.play().catch(() => showWarning("无法播放该地址"));
    } else {
      v.pause();
    }
  };

  const previewTitle =
    selectedPending?.name ||
    selectedEpisode?.playRow.name ||
    selectedEpisode?.displayName ||
    "请选择左侧视频";

  return (
    <>
      <AlertMessage config={alertConfig} onClose={hideAlert} zIndex={ALERT_Z_INDEX.MY_WORK} />

      <AutoModel
        title="确定切换吗？"
        open={subtitleSwitchOpen}
        onClose={handleSubtitleSwitchCancel}
        size="sm"
        footerRight={<>
          <Button onClick={handleSubtitleSwitchCancel}>取消</Button>
          <Button type="primary" onClick={handleSubtitleSwitchConfirm}>确定</Button>
        </>}
      >
        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: "#333" }}>
          此操作会清空当前字幕方式下已配置的信息。
        </p>
      </AutoModel>

      <AutoModel
        title="匹配不成功"
        open={subtitleMatchOpen}
        onClose={handleSubtitleMatchCancel}
        size="md"
        footerRight={<>
          <Button onClick={handleSubtitleMatchCancel}>取消</Button>
          <Button type="primary" loading={uploadBusy} onClick={() => void handleSubtitleMatchConfirm()}>
            确定
          </Button>
        </>}
      >
        {subtitleMatchInfo ? (
          <div style={{ padding: "8px 0", fontSize: 14, lineHeight: 1.6, color: "#333" }}>
            <p style={{ marginBottom: 16 }}>检测到当前多传了以下字幕文件：</p>
            <div
              style={{
                backgroundColor: "#fff2f0",
                border: "1px solid #ffccc7",
                padding: 12,
                borderRadius: 6,
                marginBottom: 16,
              }}
            >
              {subtitleMatchInfo.invalidFiles.map((invalid, index) => (
                <p key={index} style={{ margin: "4px 0", color: "#333", fontSize: 14 }}>
                  {invalid.name}.srt
                </p>
              ))}
            </div>
            <p style={{ margin: 0, color: "#666" }}>是否删除该字幕文件？</p>
          </div>
        ) : null}
      </AutoModel>

      <PremiumAutoSubtitleModal
        key={autoSubtitleModalKey}
        ref={autoSubtitleModalRef}
        open={autoSubtitleModalOpen}
        onClose={() => setAutoSubtitleModalOpen(false)}
        videoFiles={videoFiles}
        setVideoFiles={setVideoFiles as React.Dispatch<React.SetStateAction<PremiumAutoSubtitleVideoRow[]>>}
        selectedVideoIndex={selectedVideoIndex}
        showWarning={showWarning}
        onParamsPreviewChange={handleAutoSubtitleParamsPreviewChange}
      />

      <div
        className="puw"
        onDragOver={(e) => {
          e.preventDefault();
        }}
        onDrop={onDropOnWorkspace}
      >
      <input
        ref={videoInputRef}
        type="file"
        accept="video/*,.mp4,.avi,.mov,.mkv,.wmv,.flv,.webm,.m4v"
        multiple
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files;
          if (f?.length) void handleVideoFiles(f);
          e.target.value = "";
        }}
      />
      <input
        ref={scriptInputRef}
        type="file"
        accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files;
          if (f?.length) void handleScriptFiles(f);
          e.target.value = "";
        }}
      />
      <input
        ref={subtitleInputRef}
        type="file"
        accept=".srt"
        multiple
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files;
          if (f?.length) void handleSubtitleFiles(f);
          e.target.value = "";
        }}
      />

      <div className="puw__toolbar">
        <div className="puw__toolbar-left">
          <PageBackLink to={backToVideoList} aria-label="返回视频列表" />
          <h3 className="puw__title">视频上传配置</h3>
        </div>
        <div className="puw__toolbar-right">
          {hasPoints ? (
            <div className="puw__process-estimate" title="以实际扣减为准">
              <CoinsEstimateIcon />
              <span className="puw__process-estimate-label">
                预计消耗 {estimatedPoints ?? 0} 积分
              </span>
            </div>
          ) : null}
          <button
            type="button"
            className="puw__primary"
            disabled={
              !seriesId || uploadBusy || videoSlotUploading || processBusy || listLoading || listSyncBusy
            }
            onClick={() => void handleProcess()}
          >
            <span className="puw__primary-icon">
              <VideoListRefactorLucideIcon name="upload" size={14} />
            </span>
            <span>{processBusy ? "提交中…" : "处理"}</span>
          </button>
        </div>
      </div>

      <div className="puw__body">
        <div className="puw__left">
          <div className="puw__left-inner">
            <div className="puw__strip">
              {!(listLoading && videoFiles.length === 0) ? (
                <div className="puw__strip-head">
                  <div className="puw__strip-batch-progress" aria-live="polite">
                    上传进度 {stripUploadStats.done}/{stripUploadStats.total}
                  </div>
                  <div
                    className="puw__add-zone"
                    role="button"
                    tabIndex={0}
                    onClick={openVideoPicker}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") openVideoPicker();
                    }}
                  >
                    <VideoListRefactorLucideIcon name="plus-circle" size={24} />
                  </div>
                </div>
              ) : null}
              <div className="puw__strip-scroll">
                <div className="puw__strip-grid">
                {listLoading && videoFiles.length === 0 ? (
                  <div className="puw__strip-loading" aria-live="polite">
                    <div className="puw__strip-loading-spinner" />
                    <p className="puw__strip-loading-text">正在加载等待处理的视频...</p>
                  </div>
                ) : (
                  <>
                    {videoFiles.slice(stripVisibleRange.start, stripVisibleRange.end).map((vf, sliceIdx) => {
                      const index = stripVisibleRange.start + sliceIdx;
                      const isLocalRow = Boolean(vf.file) && !vf.episode;
                  if (isLocalRow) {
                    const selected = selectedVideoIndex === index;
                    /** 仅用远端封面或本地抽帧封面；previewUrl 为视频 blob，不可作 img/src（否则会长时间裂图直到抽帧完成） */
                    const imageCoverSrc = vf.remoteCoverUrl || vf.thumbnailUrl || "";
                    return (
                      <div
                        key={vf.uid}
                        role="button"
                        tabIndex={0}
                        className={`puw__card puw__card--pending${
                          vf.status === "error" ? " puw__card--error" : ""
                        }${vf.status === "queued" ? " puw__card--queued" : ""}${
                          vf.status === "uploaded" ? " puw__card--uploaded" : ""
                        }${selected ? " puw__card--selected" : ""}`}
                        onClick={() => setSelectedVideoIndex(index)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") setSelectedVideoIndex(index);
                        }}
                      >
                        <div
                          className={`puw__card-media${imageCoverSrc ? "" : " puw__card-media--no-cover"}`}
                        >
                          {imageCoverSrc ? (
                            <>
                              {/** 模糊层也用 img：与主图同源加载；仅靠 CSS background-image 时跨域等场景下背景不画、主图仍显示，两侧会像「没有毛玻璃」 */}
                              <img
                                className="puw__card-media-blur puw__card-media-blur--gpu"
                                src={imageCoverSrc}
                                alt=""
                                aria-hidden
                                loading="lazy"
                                decoding="async"
                              />
                              <img
                                className="puw__card-media-img"
                                src={imageCoverSrc}
                                alt={vf.name}
                                loading="lazy"
                                decoding="async"
                              />
                            </>
                          ) : (
                            <div className="puw__card-media-placeholder" aria-hidden>
                              <IconVideo style={{ color: "#9ca3af" }} />
                            </div>
                          )}
                          <div className="puw__card-media-fg" aria-hidden>
                            {selected ? (
                              <span style={{ color: "rgba(255,255,255,0.85)" }}>
                                <VideoListRefactorLucideIcon name="play" size={28} />
                              </span>
                            ) : imageCoverSrc ? (
                              <IconVideo style={{ color: "rgba(255,255,255,0.65)" }} />
                            ) : (
                              <IconVideo style={{ color: "#9ca3af" }} />
                            )}
                          </div>
                          {vf.status === "uploading" ? (
                            <div className="puw__card-upload-overlay">
                              <UploadProgressOverlay percent={vf.progress} />
                            </div>
                          ) : null}
                          {vf.status === "queued" ? (
                            <div className="puw__card-upload-overlay">
                              <UploadProgressOverlay muted showBar={false} message="排队中…" />
                            </div>
                          ) : null}
                          {vf.status === "error" ? (
                            <div className="puw__card-upload-overlay">
                              <UploadProgressOverlay
                                muted
                                showBar={false}
                                message={(vf.errorMessage || "上传失败").slice(0, 48)}
                              />
                            </div>
                          ) : null}
                        </div>
                        <div className="puw__card-bottom">
                          <span className="puw__card-time">
                            {vf.status === "uploading"
                              ? "上传中"
                              : vf.status === "queued"
                                ? "排队"
                                : vf.status === "uploaded"
                                  ? vf.durationSeconds != null && vf.durationSeconds > 0
                                    ? formatDuration(vf.durationSeconds)
                                    : "已上传"
                                  : "上传失败"}
                          </span>
                        </div>
                        {selected ? (
                          <div className="puw__selected-overlay">
                            <div className="puw__selected-badge">
                              <IconCheck size={16} />
                            </div>
                          </div>
                        ) : null}
                        {vf.status === "uploaded" && vf.resourceId != null && canDelete ? (
                          <button
                            type="button"
                            className="puw__card-del"
                            aria-label="删除"
                            onClick={(e) =>
                              void handleDeleteUploadedPending(e, vf.uid, vf.resourceId as number)
                            }
                          >
                            <VideoListRefactorLucideIcon name="trash-2" size={14} className="puw__card-del-icon" />
                          </button>
                        ) : vf.status !== "uploaded" ? (
                          <button
                            type="button"
                            className="puw__card-del"
                            aria-label="移除"
                            onClick={(e) => handleRemovePendingStrip(e, vf.uid)}
                          >
                            <VideoListRefactorLucideIcon name="trash-2" size={14} className="puw__card-del-icon" />
                          </button>
                        ) : null}
                      </div>
                    );
                  }
                  const item = vf.episode!;
                  const selected = selectedVideoIndex === index;
                  const hasStripVisualCover = Boolean(item.coverUrl || vf.thumbnailUrl || vf.previewUrl);
                  return (
                    <div
                      key={vf.uid}
                      className={`puw__card${selected ? " puw__card--selected" : ""}`}
                      onClick={() => setSelectedVideoIndex(index)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") setSelectedVideoIndex(index);
                      }}
                    >
                      <div
                        className={`puw__card-media${item.coverUrl ? "" : " puw__card-media--no-cover"}`}
                      >
                        {item.coverUrl ? (
                          <>
                            <img
                              className="puw__card-media-blur puw__card-media-blur--gpu"
                              src={item.coverUrl}
                              alt=""
                              aria-hidden
                              loading="lazy"
                              decoding="async"
                            />
                            <img className="puw__card-media-img" src={item.coverUrl} alt={item.displayName} loading="lazy" />
                          </>
                        ) : (
                          <div className="puw__card-media-placeholder" aria-hidden>
                            <IconVideo style={{ color: "#9ca3af" }} />
                          </div>
                        )}
                        <div className="puw__card-media-fg" aria-hidden>
                          {selected ? (
                            <span style={{ color: "rgba(255,255,255,0.85)" }}>
                              <VideoListRefactorLucideIcon name="play" size={28} />
                            </span>
                          ) : hasStripVisualCover ? (
                            <IconVideo style={{ color: "rgba(255,255,255,0.65)" }} />
                          ) : (
                            <IconVideo style={{ color: "#9ca3af" }} />
                          )}
                        </div>
                      </div>
                      <div className="puw__card-bottom">
                        <span className="puw__card-time">{item.durationLabel}</span>
                      </div>
                      {selected ? (
                        <div className="puw__selected-overlay">
                          <div className="puw__selected-badge">
                            <IconCheck size={16} />
                          </div>
                        </div>
                      ) : null}
                      {canDelete ? (
                        <button
                          type="button"
                          className="puw__card-del"
                          aria-label="删除"
                          onClick={(e) => void handleDeleteEpisode(e, item.id)}
                        >
                          <VideoListRefactorLucideIcon name="trash-2" size={14} className="puw__card-del-icon" />
                        </button>
                      ) : null}
                    </div>
                  );
                    })}
                    {videoFiles.length > stripVisibleRange.end ? (
                      <button
                        type="button"
                        className="puw__strip-load-more"
                        onClick={() =>
                          setStripVisibleRange((prev) => ({
                            start: prev.start,
                            end: Math.min(prev.end + STRIP_PAGE_SIZE, videoFiles.length),
                          }))
                        }
                      >
                        点击加载剩余{videoFiles.length - stripVisibleRange.end}个视频
                      </button>
                    ) : null}
                  </>
                )}
                </div>
              </div>
            </div>

            <div className="puw__preview-wrap">
              <div className="puw__preview-shell" ref={previewShellRef}>
                <div className="puw__preview-stage">
                  <div className="puw__preview-grid" />
                  {playUrl ? (
                    <video
                      ref={videoRef}
                      src={playUrl}
                      className="puw__preview-video"
                      playsInline
                      onTimeUpdate={onPreviewTimeUpdate}
                      onLoadedMetadata={onPreviewTimeUpdate}
                      onPlay={() => setPreviewPlaying(true)}
                      onPause={() => setPreviewPlaying(false)}
                      onEnded={() => setPreviewPlaying(false)}
                      onClick={() => togglePreviewPlayPause()}
                    />
                  ) : null}
                  <button
                    type="button"
                    className={`puw__play-hit${playUrl && previewPlaying ? " puw__play-hit--hidden" : ""}`}
                    aria-label={playUrl ? (previewPlaying ? "暂停" : "播放") : "无预览地址"}
                    onClick={() => togglePreviewPlayPause()}
                    disabled={!playUrl}
                  >
                    <VideoListRefactorLucideIcon name="play" size={32} />
                  </button>
                  <div className="puw__preview-filename">
                    <span className="puw__preview-dot" />
                    <p className="puw__preview-name">{previewTitle}</p>
                  </div>
                </div>
                <div className="puw__controls">
                  <div
                    ref={progressTrackRef}
                    className={`puw__progress${previewSeekEnabled ? " puw__progress--seekable" : ""}`}
                    role={previewSeekEnabled ? "slider" : undefined}
                    tabIndex={previewSeekEnabled ? 0 : undefined}
                    aria-valuemin={previewSeekEnabled ? 0 : undefined}
                    aria-valuemax={previewSeekEnabled ? 100 : undefined}
                    aria-valuenow={previewSeekEnabled ? previewBarPercent : undefined}
                    aria-disabled={!previewSeekEnabled}
                    onPointerDown={onProgressTrackPointerDown}
                    onPointerMove={onProgressTrackPointerMove}
                    onPointerUp={onProgressTrackPointerUp}
                    onPointerCancel={onProgressTrackPointerUp}
                    onKeyDown={onProgressTrackKeyDown}
                  >
                    <div
                      className="puw__progress-fill"
                      style={{
                        width: `${previewBarPercent}%`,
                      }}
                    />
                  </div>
                  <div className="puw__controls-row">
                    <div className="puw__controls-left">
                      <button
                        type="button"
                        className="puw__icon-btn"
                        aria-label={playUrl ? (previewPlaying ? "暂停" : "播放") : "无预览"}
                        onClick={() => togglePreviewPlayPause()}
                        disabled={!playUrl}
                      >
                        <VideoListRefactorLucideIcon name={previewPlaying ? "pause" : "play"} size={16} />
                      </button>
                      <span className="puw__time">
                        {formatClock(previewTime.cur)} / {formatClock(previewTime.total || selectedEpisode?.durationSeconds || 0)}
                      </span>
                    </div>
                    <button type="button" className="puw__icon-btn" aria-label="全屏" onClick={() => void handleFullscreen()}>
                      <IconMaximize size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <aside className="puw__right">
          <div className="puw__right-inner">
            <section className="puw__panel">
              <div className="puw__panel-head">
                <h4 className="puw__panel-title">上传剧本</h4>
                <span className="puw__optional">可选</span>
              </div>
              <div
                className={`puw__zone puw__upload-zone${scriptPanelFiles.length > 0 ? " puw__upload-zone--has-files" : ""}`}
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (fromQuick) {
                    showWarning("AI速译剧集请使用速译工作台上传剧本");
                    return;
                  }
                  scriptInputRef.current?.click();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") scriptInputRef.current?.click();
                }}
              >
                <div className="puw__zone-icon-wrap">
                  <IconFileText />
                </div>
                <p className="puw__zone-line">
                  {scriptPanelFiles.length > 0 ? "点击重新上传剧本文件" : "点击上传剧本文件"}
                </p>
                <p className="puw__zone-hint">支持 doc 和 docx 格式</p>
              </div>
              {scriptPanelFiles.length > 0 ? (
                <ul className="puw__script-list" aria-label="已上传剧本">
                  {scriptPanelFiles.map((sf) => (
                    <li key={sf.uid} className="puw__script-row">
                      <span className="puw__script-name" title={sf.name}>
                        {sf.name}
                      </span>
                      <span
                        className={`puw__script-status puw__script-status--${sf.status}`}
                        aria-live={sf.status === "uploading" ? "polite" : undefined}
                      >
                        {sf.status === "uploading" ? "上传中" : sf.status === "done" ? "已上传" : "上传失败"}
                      </span>
                      <button
                        type="button"
                        className="puw__script-remove"
                        aria-label={sf.status === "uploading" ? "取消上传" : "移除"}
                        onClick={(e) => void handleRemoveScriptPanelRow(e, sf.uid)}
                      >
                        <VideoListRefactorLucideIcon name="trash-2" size={14} />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
              <p className="puw__note">
                <IconInfo size={12} className="puw__note-icon" />
                <span>确保文档文件名与剧集名称一致</span>
              </p>
            </section>

            <section className="puw__panel puw__panel--grow">
              <div className="puw__panel-head">
                <h4 className="puw__panel-title">上传字幕</h4>
                <span className="puw__required">必选</span>
              </div>
              <div className="puw__tabs puw__tabs--subtitle">
                <button
                  type="button"
                  className={`puw__tab${subtitleMode === "manual" ? " puw__tab--active" : ""}`}
                  onClick={() => handleSubtitleModeSwitchClick("manual")}
                  disabled={fromQuick}
                >
                  <VideoListRefactorLucideIcon name="upload" size={14} />
                  <span>上传原剧字幕</span>
                </button>
                <button
                  type="button"
                  className={`puw__tab${subtitleMode === "auto" ? " puw__tab--active" : ""}`}
                  onClick={() => {
                    if (fromQuick) {
                      showWarning("AI速译剧集不支持在此页自动识别字幕");
                      return;
                    }
                    handleSubtitleModeSwitchClick("auto");
                  }}
                  disabled={fromQuick}
                >
                  <IconSparkles size={14} />
                  <span>自动识别字幕</span>
                </button>
              </div>
              {subtitleMode === "manual" ? (
                <>
                  <div
                    className={`puw__zone puw__upload-zone${
                      subtitlePanelFiles.length > 0 ? " puw__upload-zone--has-files" : ""
                    }`}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      subtitleInputRef.current?.click();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") subtitleInputRef.current?.click();
                    }}
                  >
                    <div className="puw__zone-icon-wrap">
                      <IconSubtitles />
                    </div>
                    <p className="puw__zone-line">
                      {subtitlePanelFiles.length > 0 ? "点击重新上传字幕文件" : "点击上传字幕文件"}
                    </p>
                    <p className="puw__zone-hint">支持 SRT 格式 (.srt)</p>
                  </div>
                  {subtitlePanelFiles.length > 0 ? (
                    <ul className="puw__script-list" aria-label="已上传字幕">
                      {subtitlePanelFiles.map((sf) => (
                        <li key={sf.uid} className="puw__script-row">
                          <span className="puw__script-name" title={sf.name}>
                            {sf.name}
                          </span>
                          <span
                            className={`puw__script-status puw__script-status--${sf.status}`}
                            aria-live={sf.status === "uploading" ? "polite" : undefined}
                          >
                            {sf.status === "uploading" ? "上传中" : sf.status === "done" ? "已上传" : "上传失败"}
                          </span>
                          <button
                            type="button"
                            className="puw__script-remove"
                            aria-label={sf.status === "uploading" ? "取消上传" : "移除"}
                            onClick={(e) => void handleRemoveSubtitlePanelRow(e, sf.uid)}
                          >
                            <VideoListRefactorLucideIcon name="trash-2" size={14} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  <p className="puw__note">
                    <IconInfo size={12} className="puw__note-icon" />
                    <span>请确保字幕文件与视频文件名称一致</span>
                  </p>
                </>
              ) : (
                <div
                  className="puw__subtitle-auto-panel"
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    if (fromQuick) {
                      showWarning("AI速译剧集不支持在此页自动识别字幕");
                      return;
                    }
                    void handleSubtitleModeSwitchClick("auto");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      if (fromQuick) {
                        showWarning("AI速译剧集不支持在此页自动识别字幕");
                        return;
                      }
                      void handleSubtitleModeSwitchClick("auto");
                    }
                  }}
                >
                  <div className="puw__subtitle-auto-row">
                    <span className="puw__subtitle-auto-label">字幕提取方式</span>
                    <span className="puw__subtitle-auto-value">{autoSubtitlePanelLabels.extractLabel}</span>
                  </div>
                  <div className="puw__subtitle-auto-row">
                    <span className="puw__subtitle-auto-label">识别区域方式</span>
                    <span className="puw__subtitle-auto-value">{autoSubtitlePanelLabels.regionLabel}</span>
                  </div>
                  <p className="puw__subtitle-auto-foot">点击此处重新打开「自动识别字幕」配置</p>
                </div>
              )}
            </section>

            <div className="puw__tip">
              <div className="puw__tip-inner">
                <div className="puw__tip-icon">
                  <IconLightbulb />
                </div>
                <div className="puw__tip-copy">
                  <p className="puw__tip-title">上传提示</p>
                  <p className="puw__tip-body">拖拽视频文件到页面可直接上传，支持批量操作</p>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
    </>
  );
};
